import { useToast } from "@chakra-ui/react";
import { Bundle, Resource } from "@medplum/fhirtypes";
import { errorToString } from "@metriport/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getSearchResults } from "../../../api/search";
import { capture } from "../../../shared/capture";
import { usePatientConsolidatedContext } from "../shared-logic/consolidated-context";
import { PatientConsolidatedStateActionType } from "./consolidated-context/reducer";
import { processBundleWithWorker } from "./useBundleGroupingWorker";

export type UseSearch = {
  onSearch: (question: string) => Promise<void>;
  isSearchingResults: boolean;
};

const PROCESS_SEARCH_QUERY_KEY = "process-search";

export function useSearch({ patientId }: { patientId: string }): UseSearch {
  const { patientConsolidatedDispatch } = usePatientConsolidatedContext();
  const toast = useToast();
  const [isSearchingResults, setIsSearchingResults] = useState(false);
  const [searchResults, setSearchResults] = useState<Bundle<Resource> | undefined>(undefined);
  const [searchQuestion, setSearchQuestion] = useState<string>("");

  const { data: processedSearchResults } = useQuery({
    queryKey: [PROCESS_SEARCH_QUERY_KEY, patientId, searchQuestion],
    queryFn: () => (searchResults ? processBundleWithWorker(searchResults) : undefined),
    enabled: !!searchResults,
    staleTime: 15 * 60 * 1000,
  });

  useEffect(() => {
    if (!processedSearchResults) return;
    patientConsolidatedDispatch({
      type: PatientConsolidatedStateActionType.setPatientsSearchResults,
      patientId,
      consolidatedResources: processedSearchResults,
    });
  }, [processedSearchResults, patientId, patientConsolidatedDispatch]);

  const onSearch = async (question: string) => {
    try {
      setIsSearchingResults(true);
      setSearchQuestion(question);
      const search = await getSearchResults(patientId, question);
      setSearchResults(search);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to search",
        status: "error",
      });
      const errorMessage = errorToString(error);
      capture.error("Failed to search", {
        extra: { patientId, context: `patient.search`, errorMessage, error },
      });
    } finally {
      setIsSearchingResults(false);
    }
  };

  return { onSearch, isSearchingResults };
}
